import { useEffect, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { WaveMark } from "@/components/ui/WaveMark";
import { Kbd } from "@/components/ui/Kbd";
import { modKey } from "@/lib/platform";
import { apiFetch } from "@/lib/apiClient";
import { type ChatAction } from "@/features/chat/chatIntent";

interface Message {
  who: "flow" | "user";
  text: string;
  citation?: string;
  action?: ChatAction;
  /** Once the action has been confirmed or cancelled, freeze the bubble. */
  actionState?: "pending" | "confirmed" | "cancelled";
}

const QUICK_CHIPS = [
  "What's running now?",
  "Who needs approval?",
  "What's today's cost?",
  "Pause all SpineOne jobs",
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ChatSidebar({ open, onClose }: Props) {
  const [messages, setMessages] = useState<Message[]>([
    {
      who: "flow",
      text: "Hey Nestor — ask me about the fleet. I can pause, explain failures, or pull today's cost. Plain English works best.",
    },
  ]);
  const [draft, setDraft] = useState("");
  const [thinking, setThinking] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) scrollerRef.current?.scrollTo({ top: 99_999 });
  }, [open, messages]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setMessages((m) => [...m, { who: "user", text: trimmed }]);
    setDraft("");
    setThinking(true);
    try {
      const reply = await apiFetch<{
        reply: string;
        citation?: string | null;
        confirmRequired?: boolean;
      }>("/flow/chat", {
        method: "POST",
        body: JSON.stringify({ message: trimmed }),
      });
      setMessages((m) => [
        ...m,
        {
          who: "flow",
          text: reply.reply,
          citation: reply.citation ?? undefined,
        },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          who: "flow",
          text:
            "Hit a snag reaching the API. " +
            (err instanceof Error ? err.message : "Try again in a moment."),
        },
      ]);
    } finally {
      setThinking(false);
    }
  };

  const resolveAction = (messageIndex: number, choice: "confirmed" | "cancelled") => {
    setMessages((prev) => {
      const copy = [...prev];
      const msg = copy[messageIndex];
      if (!msg || !msg.action) return prev;
      const newMsg: Message = { ...msg, actionState: choice };
      copy[messageIndex] = newMsg;
      if (choice === "confirmed") {
        runAction(msg.action);
        copy.push({
          who: "flow",
          text: summarizeResult(msg.action),
        });
      } else {
        copy.push({ who: "flow", text: "Got it — left them running." });
      }
      return copy;
    });
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[150] bg-ink-1/30 transition-opacity"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed right-0 top-0 z-[151] flex h-full w-[400px] flex-col border-l border-paper-line bg-paper-1 shadow-xl"
        role="dialog"
        aria-label="Flow chat sidebar"
      >
        <header className="flex items-center gap-2 border-b border-paper-line bg-paper-0 p-4">
          <WaveMark size={22} />
          <div className="flex-1">
            <div className="font-serif italic text-md text-teal-500">Flow</div>
            <div className="text-xs text-ink-3">online · connected to all 4 entities</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm p-1 text-ink-3 hover:bg-paper-tint"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex flex-wrap gap-1.5 border-b border-paper-line p-3">
          {QUICK_CHIPS.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setDraft(q)}
              className="rounded-pill border border-paper-line bg-paper-2 px-2.5 py-1 text-[11px] text-ink-2 hover:bg-paper-tint"
            >
              {q}
            </button>
          ))}
        </div>

        <div ref={scrollerRef} className="flex-1 space-y-3 overflow-auto p-4">
          {messages.map((m, i) => (
            <Bubble
              key={i}
              msg={m}
              onConfirm={() => resolveAction(i, "confirmed")}
              onCancel={() => resolveAction(i, "cancelled")}
            />
          ))}
          {thinking && (
            <div className="flex items-center gap-2 text-xs text-ink-3">
              <WaveMark size={18} />
              <span className="italic">thinking…</span>
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
          className="border-t border-paper-line bg-paper-2 p-3"
        >
          <div className="flex items-end gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Ask Flow…"
              className="flex-1 rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-2 text-sm text-ink-1 placeholder:text-ink-4 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button
              type="submit"
              className="flex items-center gap-1 rounded-sm border border-teal-600 bg-teal-500 px-3 py-2 text-sm text-paper-3 hover:bg-teal-600"
            >
              <Send size={12} />
            </button>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-ink-4">
            <span>
              <Kbd>{modKey}</Kbd> <Kbd>K</Kbd> to toggle
            </span>
            <span>Flow sees only your entities.</span>
          </div>
        </form>
      </aside>
    </>
  );
}

function runAction(_action: ChatAction) {
  // Action execution goes through /api/flow/chat's confirm flow when
  // the backend returns confirmRequired. Left as a no-op locally until
  // the confirmable action endpoint lands on the API.
}

function summarizeResult(action: ChatAction): string {
  if (action.kind === "pause_entity") {
    const n = action.jobIds.length;
    return `Done — paused ${n} job${n === 1 ? "" : "s"}. They'll show up in Recent as cancelled.`;
  }
  return "Done.";
}

function Bubble({
  msg,
  onConfirm,
  onCancel,
}: {
  msg: Message;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const isFlow = msg.who === "flow";
  return (
    <div className={["flex gap-2", isFlow ? "" : "flex-row-reverse"].join(" ")}>
      {isFlow ? (
        <WaveMark size={26} />
      ) : (
        <div
          aria-hidden
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-teal-500 text-[10px] font-medium text-paper-3"
        >
          NP
        </div>
      )}
      <div
        className={[
          "max-w-[280px] rounded-md p-2.5 text-sm",
          isFlow
            ? "border border-paper-line bg-paper-2 text-ink-1"
            : "border border-teal-600 bg-teal-500 text-paper-3",
        ].join(" ")}
      >
        <div className="whitespace-pre-wrap">{msg.text}</div>
        {msg.citation && isFlow && (
          <div className="mt-2 border-t border-dashed border-paper-line pt-2 font-mono text-[10px] text-ink-3">
            {msg.citation}
          </div>
        )}
        {msg.action && isFlow && msg.actionState === "pending" && (
          <div className="mt-2 flex gap-1.5">
            <button
              type="button"
              onClick={onConfirm}
              className="flex-1 rounded-xs border border-teal-600 bg-teal-500 px-2 py-1 text-[11px] font-medium text-paper-3 hover:bg-teal-600"
            >
              {msg.action.label}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-xs border border-paper-line-strong bg-paper-3 px-2 py-1 text-[11px] text-ink-1 hover:bg-paper-tint"
            >
              Cancel
            </button>
          </div>
        )}
        {msg.action && isFlow && msg.actionState && msg.actionState !== "pending" && (
          <div className="mt-2 text-[11px] italic text-ink-3">
            {msg.actionState === "confirmed" ? "Confirmed." : "Cancelled."}
          </div>
        )}
      </div>
    </div>
  );
}
